import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Loader2,
  Undo2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'

interface RefundDialogProps {
  bookingId: string
  totalPaid: number
  totalRefunded: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

type RefundState = 'input' | 'loading' | 'success' | 'error'

export function RefundDialog({
  bookingId,
  totalPaid,
  totalRefunded,
  open,
  onOpenChange,
  onSuccess,
}: RefundDialogProps) {
  const queryClient = useQueryClient()

  const maxRefundable = Math.max(0, totalPaid - totalRefunded)
  const [amount, setAmount] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [refundState, setRefundState] = useState<RefundState>('input')
  const [errorMessage, setErrorMessage] = useState('')
  const [refundedAmount, setRefundedAmount] = useState(0)

  const resetForm = () => {
    setAmount(null)
    setReason('')
    setRefundState('input')
    setErrorMessage('')
    setRefundedAmount(0)
  }

  const refundMutation = useMutation({
    mutationFn: async () => {
      const refundAmount = amount ?? 0
      if (refundAmount <= 0) {
        throw new Error('Please enter a valid amount greater than 0')
      }

      if (refundAmount > maxRefundable) {
        throw new Error(`Refund amount cannot exceed ${formatCurrency(maxRefundable)}`)
      }

      setRefundState('loading')

      // Get the current session for auth
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('You must be logged in to process refunds')
      }

      // Call the edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refund-booking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            booking_id: bookingId,
            amount: refundAmount,
            reason: reason || undefined,
          }),
        }
      )

      let data
      try {
        data = await response.json()
      } catch {
        // If we can't parse JSON, the edge function might not be deployed
        throw new Error(
          'Unable to connect to refund service. Please ensure edge functions are deployed.'
        )
      }

      if (!response.ok) {
        // Provide helpful error messages for common issues
        if (data.code === 'no_payments') {
          throw new Error(
            'No payment found for this booking. There is nothing to refund.'
          )
        }
        if (data.code === 'insufficient_refundable') {
          throw new Error(data.error || 'Refund amount exceeds available refundable balance.')
        }
        if (data.code === 'already_refunded') {
          throw new Error('This payment has already been fully refunded.')
        }
        throw new Error(data.error || 'Failed to process refund')
      }

      return { amount: refundAmount, data }
    },
    onSuccess: (result) => {
      setRefundedAmount(result.amount)
      setRefundState('success')
      queryClient.invalidateQueries({ queryKey: ['booking-payments', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['booking-detail', bookingId] })
      onSuccess?.()
    },
    onError: (error: Error) => {
      setErrorMessage(error.message)
      setRefundState('error')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    refundMutation.mutate()
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  const handleRetry = () => {
    setRefundState('input')
    setErrorMessage('')
  }

  const currentAmount = amount ?? 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        {refundState === 'input' && (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Undo2 className="h-5 w-5" />
                Issue Refund
              </DialogTitle>
              <DialogDescription>
                Refund an amount to the customer's original payment method.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* Payment Summary */}
              <div className="p-3 bg-muted rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Paid</span>
                  <span className="font-medium">{formatCurrency(totalPaid)}</span>
                </div>
                {totalRefunded > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Already Refunded</span>
                    <span className="font-medium text-orange-600">
                      -{formatCurrency(totalRefunded)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-muted-foreground">Max Refundable</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(maxRefundable)}
                  </span>
                </div>
              </div>

              {/* Amount */}
              <div className="grid gap-2">
                <Label htmlFor="refundAmount">Amount to Refund</Label>
                <CurrencyInput
                  value={amount}
                  onChange={setAmount}
                  placeholder="0.00"
                  max={maxRefundable}
                />
                {currentAmount > maxRefundable && (
                  <p className="text-xs text-destructive">
                    Amount exceeds maximum refundable ({formatCurrency(maxRefundable)})
                  </p>
                )}
              </div>

              {/* Reason */}
              <div className="grid gap-2">
                <Label htmlFor="refundReason">Reason (optional)</Label>
                <Textarea
                  id="refundReason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Service not rendered, customer complaint..."
                  rows={2}
                />
              </div>

              {/* Warning */}
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Refund will be processed immediately</AlertTitle>
                <AlertDescription>
                  {formatCurrency(currentAmount)} will be refunded to the
                  customer's original payment method. This action cannot be undone.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={!amount || currentAmount <= 0 || currentAmount > maxRefundable}
              >
                Refund {formatCurrency(currentAmount)}
              </Button>
            </DialogFooter>
          </form>
        )}

        {refundState === 'loading' && (
          <div className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-lg font-medium">Processing refund...</p>
            <p className="text-sm text-muted-foreground">
              Please wait while we process the refund.
            </p>
          </div>
        )}

        {refundState === 'success' && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium">Refund Successful</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {formatCurrency(refundedAmount)} has been refunded to the customer.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              The refund may take 5-10 business days to appear on the customer's statement.
            </p>
            <Button className="mt-6" onClick={handleClose}>
              Close
            </Button>
          </div>
        )}

        {refundState === 'error' && (
          <div className="py-8">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-center">Refund Failed</h3>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              {errorMessage}
            </p>
            <div className="flex gap-2 mt-6 justify-center">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
